import { useEffect, useId, useRef } from 'react'
import { useDismissOnOutside } from '../../../hooks/useDismissOnOutside'
import { ChevronDown, X } from 'lucide-react'
import { IconButton } from '../primitives/IconButton'
import { Input } from './Controls'
import { Field } from './Field'
import styles from './SearchCombobox.module.css'
import { useComboboxState } from './useComboboxState'

/**
 * A search box whose results are an ARIA listbox: arrow keys move through the
 * options (wrapping), Enter picks one, and picking keeps the query and the
 * results so several options can be picked from one search. `noun` names the
 * results in the status, toggle and "Load more" copy ("market" → "3 market
 * results", "Load more markets"). `status` is idle, loading, success or error.
 * `fallback` shows under a failed or empty search: another way forward.
 * `controls` render above the field, inside the combobox, so using them
 * (a scope select) keeps the results open.
 */
export function SearchCombobox({
  label,
  help,
  placeholder,
  noun = 'item',
  disabled = false,
  query,
  onQueryChange,
  items = [],
  getItemKey,
  renderItem,
  isSelected = () => false,
  onSelect,
  status = 'idle',
  error,
  warnings = [],
  emptyMessage,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  notes,
  fallback,
  controls,
}) {
  const generatedId = useId()
  const resultsId = `${generatedId}-results`
  const optionId = (index) => `${generatedId}-option-${index}`
  const containerRef = useRef(null)
  const inputRef = useRef(null)
  // Collapsing hides the results but keeps the query, so picking several
  // options from one search still works: clicking the input or typing reopens them.
  const combobox = useComboboxState(items.length)
  const { activeIndex, clearActive } = combobox
  const plural = `${noun}s`
  const Noun = `${noun.charAt(0).toUpperCase()}${noun.slice(1)}`

  const resultsOpen = combobox.open

  useEffect(() => {
    clearActive()
  }, [query, items.length, clearActive])

  // On click, not mousedown: the list is inline, so collapsing it moves the page.
  useDismissOnOutside([containerRef], combobox.collapse, { enabled: resultsOpen, event: 'click' })

  const toggleResults = () => {
    combobox.toggle()
    inputRef.current?.focus({ preventScroll: true })
  }

  const clearSearch = () => {
    combobox.expand()
    onQueryChange('')
    inputRef.current?.focus({ preventScroll: true })
  }

  const selectItem = (item) => {
    onSelect(item)
    inputRef.current?.focus({ preventScroll: true })
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      // First Escape steps out of the list; the next one closes the search.
      // Results, "no matches" and "searching" all exist only for a query,
      // so clearing the query is the close.
      if (activeIndex >= 0) {
        clearActive()
      } else if (query) {
        onQueryChange('')
      }
      return
    }
    if (event.key === 'Enter') {
      // The search never submits a surrounding form (affiliate links): Enter
      // picks the highlighted option, reopens a collapsed list, or does nothing.
      event.preventDefault()
      const item = resultsOpen ? items[activeIndex] : undefined
      if (item) selectItem(item)
      else combobox.expand()
      return
    }
    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && items.length) {
      event.preventDefault()
      const next = combobox.move(event.key === 'ArrowDown' ? 1 : -1)
      // Scrolled here rather than in an effect on activeIndex: hover sets it
      // too, and scrolling on hover fights the wheel while over the list.
      if (next >= 0) document.getElementById(optionId(next))?.scrollIntoView?.({ block: 'nearest' })
    }
  }

  return (
    <div className={styles.combobox} ref={containerRef}>
      {controls}
      <Field id={`${generatedId}-field`} label={label} help={help}>
        <Input
          type="text"
          ref={inputRef}
          actions={[
            query && !disabled ? (
              <IconButton key="clear" variant="ghost" label="Clear search" onClick={clearSearch}>
                <X size={16} aria-hidden="true" />
              </IconButton>
            ) : null,
            items.length > 0 ? (
              <IconButton
                key="toggle"
                className={resultsOpen ? styles.toggleExpanded : undefined}
                variant="ghost"
                label={resultsOpen ? `Hide ${noun} results` : `Show ${noun} results`}
                aria-expanded={resultsOpen}
                aria-controls={resultsOpen ? resultsId : undefined}
                onClick={toggleResults}
              >
                <span className={styles.chevron} aria-hidden="true">
                  <ChevronDown size={16} />
                </span>
              </IconButton>
            ) : null,
          ]}
          disabled={disabled}
          value={query}
          onChange={(event) => {
            combobox.expand()
            onQueryChange(event.target.value)
          }}
          onClick={combobox.expand}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          role="combobox"
          aria-expanded={resultsOpen}
          aria-haspopup="listbox"
          aria-controls={resultsOpen ? resultsId : undefined}
          aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
        />
      </Field>
      <div className={styles.status} role="status" aria-live="polite">
        {loadingMore ? `Loading more ${plural}…` : null}
        {!loadingMore && status === 'loading' ? `Searching ${plural}…` : null}
        {!loadingMore && status === 'success' ? `${items.length} ${noun} result${items.length === 1 ? '' : 's'}` : null}
      </div>
      {notes}
      {status === 'error' ? (
        <div className={styles.error} role="alert">{error || `${Noun} search failed. Try again.`}</div>
      ) : null}
      {warnings.map((warning) => <p className={styles.warning} key={warning}>{warning}</p>)}
      {status === 'success' && items.length === 0 ? (
        <div className={styles.empty}>{emptyMessage || `No ${plural} matched. Try another search.`}</div>
      ) : null}
      {status === 'error' || (status === 'success' && items.length === 0) ? fallback : null}
      {resultsOpen ? (
        <>
          <ul id={resultsId} role="listbox" className={styles.results} aria-label={`${Noun} search results`}>
            {items.map((item, index) => {
              const selected = isSelected(item)
              const active = activeIndex === index
              return (
                <li key={getItemKey(item)} role="presentation">
                  <button
                    id={optionId(index)}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={[styles.option, selected ? styles.optionSelected : '', active ? styles.optionActive : ''].filter(Boolean).join(' ')}
                    onMouseEnter={() => combobox.highlight(index)}
                    onClick={() => selectItem(item)}
                  >
                    {renderItem(item)}
                  </button>
                </li>
              )
            })}
          </ul>
          {hasMore ? (
            <button
              type="button"
              className={styles.loadMore}
              aria-controls={resultsId}
              onClick={() => onLoadMore()}
              disabled={loadingMore}
            >
              {loadingMore ? `Loading more ${plural}…` : `Load more ${plural}`}
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  )
}
